<template>
  <div v-if="!isAllDay" class="duration-picker-wrapper" @keydown.enter.stop>
    <VSelect
      v-if="isEditable"
      v-model="selectedDuration"
      :options="displayOptions"
      :clearable="false"
      :searchable="true"
      placeholder="Duration"
      :class="['duration-select', { isEdited }]"
      @update:model-value="onSelected"
      @search="onSearch"
    >
      <template #open-indicator="{ attributes }">
        <span v-bind="attributes"><ChevronDownIcon /></span>
      </template>
    </VSelect>
    <BaseButton
      v-else
      kind="auxiliary"
      state="disabled"
      :text="selectedDuration"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, toRefs, watch } from "vue";
import VSelect from "vue-select";

import BaseButton from "@/components/base/BaseButton.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";

export function formatDuration(minutes: number): string {
  const rounded = Math.round(minutes);
  if (rounded < 60) {
    return `${rounded}m`;
  }
  const h = Math.floor(rounded / 60);
  const m = rounded % 60;
  if (m === 0) {
    return `${h}h`;
  }
  return `${h}h${m}`;
}

export function parseDuration(input: string): number | null {
  const trimmed = input.trim().toLowerCase();

  const hm = trimmed.match(/^(\d+)\s*h\s*(\d+)\s*m?$/);
  if (hm) {
    return parseInt(hm[1], 10) * 60 + parseInt(hm[2], 10);
  }

  const h = trimmed.match(/^(\d+(?:\.\d+)?)\s*h$/);
  if (h) {
    return Math.round(parseFloat(h[1]) * 60);
  }

  const m = trimmed.match(/^(\d+)\s*m$/);
  if (m) {
    return parseInt(m[1], 10);
  }

  const colon = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (colon) {
    return parseInt(colon[1], 10) * 60 + parseInt(colon[2], 10);
  }

  const num = trimmed.match(/^(\d+)$/);
  if (num) {
    return parseInt(num[1], 10);
  }

  return null;
}

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120, 180, 240, 300, 360, 480];

export default defineComponent({
  components: {
    BaseButton,
    ChevronDownIcon,
    VSelect,
  },
  props: {
    durationMinutes: { type: Number, required: false, default: undefined },
    isAllDay: { type: Boolean, required: true },
    isEditable: { type: Boolean, required: true },
    isEdited: { type: Boolean, required: false, default: false },
  },
  emits: {
    "update:duration": (_minutes: number) => true,
  },
  setup(props, { emit }) {
    const { durationMinutes } = toRefs(props);
    const selectedDuration = ref<string>("");
    const searchText = ref<string>("");

    const durationOptions = computed(() =>
      DURATION_OPTIONS.map((mins) => formatDuration(mins))
    );

    const displayOptions = ref<string[]>([]);

    watch(
      durationOptions,
      (newOptions) => {
        displayOptions.value = [...newOptions];
      },
      { immediate: true }
    );

    watch(
      durationMinutes,
      (newVal) => {
        if (newVal !== undefined && newVal >= 0) {
          selectedDuration.value = formatDuration(newVal);
        } else {
          selectedDuration.value = "";
        }
      },
      { immediate: true }
    );

    const onSelected = (value: string) => {
      if (value) {
        const parsed = parseDuration(value);
        if (parsed && parsed > 0) {
          emit("update:duration", parsed);
        }
      }
    };

    const onSearch = (search: string) => {
      searchText.value = search;
      const options = [...durationOptions.value];

      if (search) {
        const parsed = parseDuration(search);
        if (parsed && parsed > 0) {
          const formatted = formatDuration(parsed);
          if (!options.includes(formatted)) {
            options.unshift(formatted);
          }
        }
      }

      displayOptions.value = options;
    };

    return {
      displayOptions,
      onSearch,
      onSelected,
      selectedDuration,
    };
  },
});
</script>

<style lang="scss" scoped>
.duration-select.v-select {
  @extend %font-caption-regular;
  min-width: 0;
  width: 70px;
}

:deep(.v-select.duration-select) {
  .vs__dropdown-toggle {
    border: none;
    box-shadow: none;
  }

  .vs__open-indicator,
  .vs__actions {
    display: none;
  }

  .vs__dropdown-menu {
    min-width: 90px;
  }

  .vs__search {
    display: block;
    @extend %font-caption-regular;
    padding-inline-start: 7px;
  }

  .vs__selected-options {
    min-width: 50px;
  }

  .vs__selected {
    border-radius: $border-radius-m;
    cursor: text;
    @include transition(background-color 100ms ease-out);
  }
}

.duration-select:not(.vs--open) {
  :deep(.vs__selected) {
    @include themed() {
      color: t($grey-40);
    }
  }

  :deep(.vs__selected:hover) {
    @include themed() {
      background-color: t($grey-10);
      color: t($primary);
    }
  }
}

.duration-select.vs--open {
  :deep(.vs__selected) {
    @include themed() {
      background-color: t($grey-15);
    }
  }
}
</style>
